import { authHeaders, endSession, currentRole, getShop } from './session';

// Both of these are per portal AND per shop, not per browser.
//
// The snapshot is what the screen falls back to when a load fails, and the two portals are handed
// different data by design: a supplier is only ever shown its own quotes. Under one key the last tab
// to load wins, so an admin signed in as a supplier in another tab - which is how quotes get entered
// on a supplier's behalf - would drop to that supplier's filtered snapshot on the first blip and see
// one supplier's prices instead of everyone's. That happened on 2026-09-30 and is why the key carries
// the portal.
//
// The shop is in the key for the same reason, one level up: two tabs signed in as the admin of two
// different shops would otherwise share a snapshot, and the first failed poll would show one shop the
// other's stock and prices. The queue is worse than a wrong screen - work queued in one shop's tab and
// replayed with another shop's pass is a write landing in the wrong business - so each entry also
// records the shop it was made in, and flushQueue refuses any entry that does not match. The key alone
// is not trusted to be right.
const QUEUE_KEY = () => `ts_offline_queue_${currentRole()}_${getShop() || 'none'}`;
const CACHE_KEY = () => `ts_data_cache_${currentRole()}_${getShop() || 'none'}`;

// The keys from before these splits. Dropped rather than adopted: nothing records which portal or shop
// wrote them, and guessing wrong is the bug this exists to remove.
try {
  localStorage.removeItem('ts_offline_queue');
  localStorage.removeItem('ts_data_cache');
  for (const role of ['admin', 'supplier']) {
    localStorage.removeItem(`ts_offline_queue_${role}`);
    localStorage.removeItem(`ts_data_cache_${role}`);
  }
} catch (e) { /* private window */ }

// Paths that create new records (POST → server assigns an ID)
const CREATE_PATHS = ['/api/products', '/api/suppliers', '/api/shipments', '/api/conversations', '/api/shipment-comments'];

const RESOURCE_MAP = {
  '/api/products': 'products',
  '/api/suppliers': 'suppliers',
  '/api/shipments': 'shipments',
  '/api/conversations': 'conversations',
  '/api/shipment-comments': 'shipmentComments',
};

// ── Queue helpers ──────────────────────────────────────────────────────────────
export function getQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY()) || '[]'); } catch { return []; }
}
function saveQueue(q) {
  localStorage.setItem(QUEUE_KEY(), JSON.stringify(q));
}

// ── Data snapshot helpers ──────────────────────────────────────────────────────
// The snapshot lives in IndexedDB, not localStorage. localStorage holds about 5 MB a site, and the admin's
// catalogue of 4,000-odd products is more than that, so the copy never fitted: first the failed write put
// an older copy over fresh data, then (2e34af95) the copy was simply dropped, which left the admin portal
// with nothing to show offline. IndexedDB is allowed a share of the disk instead, and stores the objects
// as they are, without turning them into one long string first.
//
// Every read and write goes through one queue, in order. Creating something offline injects it into the
// snapshot and then reloads, and the reload reads the snapshot: in parallel the read could land before
// the write and the new item would be missing from the screen.
const DB_NAME = 'ts_offline';
const STORE = 'snapshots';
let dbPromise = null;
function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') return reject(new Error('no IndexedDB'));
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
}
function idb(mode, act) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = act(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }));
}
let cacheChain = Promise.resolve();
function inOrder(fn) {
  const run = cacheChain.then(fn, fn);
  cacheChain = run.catch(() => {});
  return run;
}

// The copies localStorage held before this. Freed rather than moved: the next load writes a fresh one.
try {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i);
    if (k && k.startsWith('ts_data_cache_')) localStorage.removeItem(k);
  }
} catch (e) { /* private window */ }

// Null when there is no copy or the browser will not keep one (some private windows): the screen then
// stays as it is, exactly as before there was a cache at all.
export function loadDataCache() {
  const key = CACHE_KEY();
  return inOrder(() => idb('readonly', st => st.get(key)).then(v => v || null, () => null));
}
// A copy that cannot be written is dropped, never left half old: the next load is fresh anyway, and a
// stale one is worse.
export function saveDataCache(data) {
  const key = CACHE_KEY();
  return inOrder(() => idb('readwrite', st => st.put({ ...data, _ts: Date.now() }, key))
    .catch(() => idb('readwrite', st => st.delete(key)).catch(() => {})));
}

// Inject a newly-created offline item into the snapshot so it shows in the UI
function injectTempIntoCache(resource, item) {
  const key = CACHE_KEY();
  return inOrder(async () => {
    const cache = await idb('readonly', st => st.get(key)).catch(() => null);
    if (!cache) return;
    cache[resource] = [...(cache[resource] || []), item];
    if (resource === 'products') {
      cache.allProducts = [...(cache.allProducts || []), item];
    }
    await idb('readwrite', st => st.put(cache, key)).catch(() => {});
  });
}

// ── Enqueue a failed mutation ──────────────────────────────────────────────────
// Returns tempId (string) for creates, null otherwise.
export function enqueueOfflineOp(path, options) {
  const method = options.method || 'POST';
  const isCreate = method === 'POST' && CREATE_PATHS.includes(path);
  const tempId = isCreate
    ? `temp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
    : null;

  const op = {
    id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    // The shop this was made in, stamped on the entry itself. flushQueue checks it rather than trusting
    // the key it was found under: a write replayed into another business is not a fault anyone can see
    // afterwards, so it is refused here even if the key says it belongs.
    shop: getShop(),
    path,
    method,
    body: options.body || null,
    tempId,
    ts: Date.now(),
  };

  const queue = getQueue();
  if (queue.length < 100) {
    queue.push(op);
    saveQueue(queue);
  }

  if (isCreate && tempId) {
    injectTempIntoCache(RESOURCE_MAP[path], { ...(options.body || {}), id: tempId, _offline: true });
  }

  return tempId;
}

// ── Flush the queue when back online ──────────────────────────────────────────
// Replays ops in order, resolving temp IDs to real server IDs as CREATEs complete.
// Calls onDone() once the queue empties successfully.
export async function flushQueue(onDone) {
  if (!navigator.onLine) return;
  const queue = getQueue();
  if (queue.length === 0) { if (onDone) onDone(); return; }

  const idMap = {}; // tempId → realId
  const remaining = [];
  let networkDown = false;
  let authFailed = false;
  const shop = getShop();

  for (const op of queue) {
    if (networkDown) { remaining.push(op); continue; }

    // Work made in another shop is dropped, not replayed and not kept. Replaying it would write into the
    // wrong business; keeping it would try again on every flush for ever. An entry from before entries
    // carried a shop (op.shop undefined) is treated the same way, because nothing records where it came
    // from and guessing is the mistake this is here to prevent.
    if (op.shop !== shop) {
      console.warn('offline queue: dropping work queued in another shop', { queued: op.shop || '(unknown)', now: shop });
      continue;
    }

    // Substitute any resolved temp IDs in path and body
    let resolvedPath = op.path;
    let resolvedBodyStr = op.body ? JSON.stringify(op.body) : null;
    for (const [tmp, real] of Object.entries(idMap)) {
      resolvedPath = resolvedPath.split(tmp).join(String(real));
      if (resolvedBodyStr) resolvedBodyStr = resolvedBodyStr.split(tmp).join(String(real));
    }

    try {
      const res = await fetch(resolvedPath, {
        method: op.method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: resolvedBodyStr,
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // Map temp ID → real server ID for subsequent ops in this queue
        if (op.tempId && data.id) idMap[op.tempId] = data.id;
      } else if (res.status === 401) {
        // The pass is no longer valid: keep this op and the rest for after the next
        // sign-in, and send the user back to the login screen.
        console.warn('[offline sync] Not signed in, pausing replay:', op.path);
        remaining.push(...queue.slice(queue.indexOf(op)));
        authFailed = true;
        break;
      } else {
        // Server rejected (4xx/5xx) — discard to avoid infinite retry
        console.warn('[offline sync] Server rejected queued op, discarding:', op.path, res.status);
      }
    } catch {
      // Still no network — stop, preserve remaining ops
      networkDown = true;
      remaining.push(op);
    }
  }

  saveQueue(remaining);
  if (authFailed) { endSession(); return; }
  if (!networkDown && onDone) onDone();
}
